import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as actions from 'containers/ProjectTools/Notes/actions';
import {
  getProjectFeatures as actionGetProjectFeatures,
  releaseNotes as actionReleaseNotes,
  weddingPermissions as actionWeddingPermissions,
} from 'containers/Projects/actions';
import {
  getNoteTemplateFolders as actionGetNoteTemplateFolders,
} from 'containers/Settings/Projects/actions';
import { getSingleProject as actionGetSingleProject } from 'containers/ProjectTools/Project/actions';
import CreateNoteModal from 'pages/Projects/Tools/Notes/CreateNote/CreateNote';
import { Loading } from 'components/Loading';
import clsx from 'clsx';
import NoteLanding from './Notes.Landing';
import Notes from './Notes';
import './note-item.scss';
import NotesViewOptions from './Notes.ViewOption';
import PrepMode from '../../components/PrepMode';
import Search from '../../components/Search';
import { NotesActionButton } from './Notes.ActionButton';

const NotesIndex = (props) => {
  const { match } = props;
  const { params } = match || {};
  const { projectId } = params || props;
  const dispatch = useDispatch();
  const notesViewOptions = useSelector((state) => state.global.notesViewOptions);
  const notes = useSelector((state) => state.projectTools.notes?.items || []);
  const searchedNotes = useSelector((state) => state.projectTools.notes.searchedItems || []);
  const notesLoading = useSelector((state) => state.projectTools.notes.notesLoading);
  const templates = useSelector((state) => state.projectTools.notes?.templates || []);
  const templateFolders = useSelector((state) => state.settings.projectSettings.noteTemplateFolders);
  const noteGroups = useSelector(state => state.projectTools.notes.groups);
  const currentProject = useSelector((state) => state.projects.currentProject);
  const error = useSelector(state => state.projectTools.notes.error);
  const weddingPermissions = useSelector(state => state.projects.weddingPermission || []);

  const getWeddingPermissionsbyName = (permissionsName) => {
    const index = weddingPermissions.find(p => p.name === permissionsName);
    if(index){
      return index.value;
    }

    return true;
  };

  const hasNoteEditPermissions = useMemo(() => getWeddingPermissionsbyName('notes.edit'), [weddingPermissions]);
  // Client must have notes.edit permission and notes or sections to be able to create a section.
  const hasSectionAddPermissions = hasNoteEditPermissions && (notes?.length || noteGroups?.length);

  const [showPrepMode, setShowPrepMode] = useState(false);
  useEffect(() => {
    if (currentProject.features) {
      setShowPrepMode(!currentProject.features.notesReleased);
    }
  }, [currentProject]);

  const {
    updateIndexPreferences,
    getNotes,
    createNote,
    createNoteFromTemplate,
    searchNote,
    getNotesTemplates,
    getNoteTemplateFolders,
    getSingleProject,
    getNoteGroups,
    getProjectFeatures,
    releaseNotes,
    getWeddingPermissions,
  } = bindActionCreators({
    ...actions, getSingleProject: actionGetSingleProject,
    getProjectFeatures: actionGetProjectFeatures, releaseNotes: actionReleaseNotes,
    getNoteTemplateFolders: actionGetNoteTemplateFolders, getWeddingPermissions: actionWeddingPermissions,
  }, dispatch);


  useEffect(() => {
    getWeddingPermissions({ weddingId: projectId });
    getProjectFeatures({ id: projectId });
    getNotes({ weddingId: projectId, notesLoading: true });
    getNotesTemplates();
    getNoteTemplateFolders();
    getSingleProject({ id: projectId });
    getNoteGroups({ weddingId: projectId });
  }, []);

  const [searchKey, setSearchValue] = useState('');
  const [isCreateModalOpened, setCreateModalOpened] = useState(false);
  const [notesItems, setNotesItems] = useState(notes);
  const [addNewGroup, setAddNewGroup] = useState(false);

  useEffect(() => {
    if (!searchKey) {
      setNotesItems(notes);
    }
  }, [notes, searchKey]);

  useEffect(() => {
    if (searchKey) {
      setNotesItems(searchedNotes);
    }
  }, [searchedNotes]);

  const createNewNote = () => {
    setCreateModalOpened(true);
  };

  const createNewGroup = () => {
    setAddNewGroup(true);
  };

  const handleCreateNote = (noteBody) => {
    if (noteBody.templatesId) {
      createNoteFromTemplate(noteBody);
    } else {
      createNote(noteBody);
    }
  };

  const handleChangeOptions = (name, value) => {
    notesViewOptions[name] = value;
    updateIndexPreferences(notesViewOptions);
  };

  const afterDismissViewOptions = () => {};

  const handleReleaseNotes = () => {
    releaseNotes({ id: projectId });
  };

  return (
    <>
      {notesLoading || !notesViewOptions.view || !noteGroups ? <Loading />
        : (
          <div className="content-container notes-container">
            <section className="page-title page-title--actions">
              <h1 className="title">Notes</h1>
              {notes.length || noteGroups.length ? (
                <Search
                  placeholderText="Search Notes"
                  fetchTableData={() => {
                    getNotes({ weddingId: projectId, notesLoading: false });
                  }}
                  searchTableData={() => {
                    searchNote({ projectId, searchKey });
                  }}
                  searchKey={searchKey}
                  setSearchValue={setSearchValue}
                />
               ) : null
               }
              <div className={clsx(
                'page-title__actions',
                !(notes.length || noteGroups.length || searchKey) && 'ml-auto',
                )}
              >
                <NotesActionButton
                  createNote={hasNoteEditPermissions ? createNewNote : null}
                  createSection={hasSectionAddPermissions ? createNewGroup : null}
                />
              </div>
            </section>
            {notes.length || noteGroups.length ? (
              <>
                <div className="index-page__top note-index__top">
                  <NotesViewOptions
                    options={notesViewOptions}
                    handleChangeOptions={handleChangeOptions}
                    afterDismissViewOptions={afterDismissViewOptions}
                  />
                </div>
                <Notes
                  items={notesItems}
                  view={notesViewOptions.view}
                  groups={noteGroups}
                  templateFolders={templateFolders}
                  projectId={projectId}
                  error={error}
                  notesEdit={getWeddingPermissionsbyName('notes.edit')}
                  searchKey={searchKey}
                  addNewGroup={addNewGroup}
                  setAddNewGroup={setAddNewGroup}
                />
              </>
            ) : (
              <NoteLanding createNote={createNewNote} />)
            }
            {showPrepMode && (
              <PrepMode
                message='This project’s notebook is in prep mode and not yet visible to your clients.'
                actionText='Release Notebook'
                action={handleReleaseNotes}
                confirmationText='Releasing notes from prep mode will make the notebook
                    visible to your clients and vendors. Any notes restricted to ORG MEMBERS ONLY will remain hidden.'
                confirmationButtonText='Release Notebook'
              />
            )
            }
          </div>
        )}
      <CreateNoteModal
        open={isCreateModalOpened}
        onClose={() => setCreateModalOpened(false)}
        weddingId={projectId}
        createNote={handleCreateNote}
        templateFolders={templateFolders ? templateFolders.map(item => ({ value: item.id, label: item.name })) : null}
        templates={templates.map(item => ({ value: item.id, label: item.name, folderId: item.noteTemplateFolderId }))}
      />
    </>
  );
};

export default NotesIndex;
